import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { MeetingDto } from "@shared/types/MeetingDto";
import { STATUS_COLORS, STATUS_LABELS } from "./constants";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

interface MeetingCalendarViewProps {
  meetings: MeetingDto[];
  onSelectMeeting: (meeting: MeetingDto) => void;
}

export function MeetingCalendarView({
  meetings,
  onSelectMeeting,
}: MeetingCalendarViewProps) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());

  const calendarMeetings = meetings.filter(
    (m) =>
      m.scheduledAt &&
      ["CONFIRMED", "AWAITING_BOOKING", "IN_PROGRESS"].includes(m.status)
  );

  const meetingsByDate = useMemo(() => {
    const map = new Map<string, MeetingDto[]>();
    calendarMeetings.forEach((m) => {
      if (!m.scheduledAt) return;
      const d = new Date(m.scheduledAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    });
    return map;
  }, [calendarMeetings]);

  const daysGrid = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const cells: (Date | null)[] = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      cells.push(null);
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push(new Date(year, month, d));
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [month, year]);

  const handlePrev = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="icon" onClick={handlePrev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-semibold">
          {MONTHS[month]} {year}
        </h3>
        <Button variant="ghost" size="icon" onClick={handleNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-px bg-border/50 rounded-lg overflow-hidden">
        {DAYS.map((day) => (
          <div
            key={day}
            className="bg-card p-2 text-center text-xs font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}

        {daysGrid.map((date, i) => {
          if (!date) {
            return <div key={`empty-${i}`} className="bg-card/50 min-h-[80px]" />;
          }

          const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          const dayMeetings = meetingsByDate.get(key) || [];
          const isToday =
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();

          return (
            <div
              key={key}
              className={cn(
                "bg-card min-h-[80px] p-1.5",
                isToday && "ring-1 ring-inset ring-primary/30"
              )}
            >
              <span
                className={cn(
                  "text-xs",
                  isToday
                    ? "font-bold text-primary"
                    : "text-muted-foreground"
                )}
              >
                {date.getDate()}
              </span>
              <div className="mt-1 space-y-0.5">
                {dayMeetings.slice(0, 3).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onSelectMeeting(m)}
                    className="w-full text-left"
                    aria-label={`Meeting: ${m.title}`}
                  >
                    <Badge
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-[10px] px-1 py-0 truncate cursor-pointer hover:opacity-80",
                        STATUS_COLORS[m.status]
                      )}
                    >
                      {m.scheduledAt &&
                        new Date(m.scheduledAt).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                        })}{" "}
                      {m.title}
                    </Badge>
                  </button>
                ))}
                {dayMeetings.length > 3 && (
                  <p className="text-[10px] text-muted-foreground px-1">
                    +{dayMeetings.length - 3} more
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
        {(["CONFIRMED", "AWAITING_BOOKING", "IN_PROGRESS"] as const).map(
          (status) => (
            <div key={status} className="flex items-center gap-1">
              <div
                className={cn(
                  "w-2 h-2 rounded-full",
                  STATUS_COLORS[status].split(" ")[0]
                )}
              />
              {STATUS_LABELS[status]}
            </div>
          )
        )}
      </div>
    </Card>
  );
}
